import { Upload } from "lucide-react";
import { useRef, type FC } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

type Props = {
  imageUrl?: string;
  initials: string;
  isUploading: boolean;
  onUpload: (file: File) => void | Promise<void>;
  size?: "default" | "lg";
  /** Square tile (as in the header pill / popover) vs. circle. */
  shape?: "circle" | "square";
  className?: string;
};

/** Avatar that opens a file picker on click and shows an upload hint on hover. */
export const AvatarUpload: FC<Props> = ({
  imageUrl,
  initials,
  isUploading,
  onUpload,
  size = "lg",
  shape = "square",
  className
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const rounded = shape === "square" ? "rounded-lg" : "rounded-full";
  const roundedRing =
    shape === "square" ? "after:rounded-lg" : "after:rounded-full";

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0] ?? null;
          event.target.value = "";
          if (file) onUpload(file);
        }}
      />
      <button
        type="button"
        aria-label="Upload client photo"
        title="Upload client photo"
        disabled={isUploading}
        onClick={() => inputRef.current?.click()}
        className={cn(
          "group/upload relative shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
          rounded,
          className
        )}
      >
        <Avatar
          size={size}
          className={cn(rounded, roundedRing, isUploading && "opacity-60")}
        >
          {imageUrl ? (
            <AvatarImage src={imageUrl} alt="" className={rounded} />
          ) : null}
          <AvatarFallback className={rounded}>{initials}</AvatarFallback>
        </Avatar>
        <span
          className={cn(
            "pointer-events-none absolute inset-0 flex items-center justify-center bg-fill-1 text-white transition-colors group-hover/upload:bg-black/30",
            rounded
          )}
        >
          <Upload className="size-3.5 opacity-0 transition-opacity group-hover/upload:opacity-100" />
        </span>
      </button>
    </>
  );
};
